import { useCallback, useEffect, useRef, useState } from 'react';

import { API_KEYS_PAGE_SIZE } from 'api/meedioBackend/apiKeys/constants';
import { listApiKeys } from 'api/meedioBackend/apiKeys/listApiKeys';
import { ApiKeyItem } from 'api/meedioBackend/apiKeys/types';
import logger from 'utils/logging/faro';

export interface ApiKeysListResult {
  items: ApiKeyItem[];
  isLoading: boolean;
  error: unknown;
  hasMore: boolean;
  loadMore: () => void;
  refresh: () => void;
}

/**
 * Loads the workspace's `status=active` API keys with lazy pagination. Refs guard
 * against concurrent/duplicate page fetches, mirroring `useRoomAttachments`.
 * Pass the backend workspace UUID (`null` while it is still resolving).
 */
const useApiKeys = (workspaceUuid: string | null): ApiKeysListResult => {
  const [items, setItems] = useState<ApiKeyItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [hasMore, setHasMore] = useState(false);

  const mountedRef = useRef(true);
  const isFetchingRef = useRef(false);
  const hasMoreRef = useRef(false);
  const nextPageRef = useRef(1);
  const loadedCountRef = useRef(0);
  const totalRef = useRef(0);
  // Bumped on every workspace switch; in-flight responses from a previous
  // workspace are discarded so stale keys never render on the new workspace.
  const epochRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchPage = useCallback(
    async (page: number, replace: boolean) => {
      if (!workspaceUuid || isFetchingRef.current) return;

      const epoch = epochRef.current;
      isFetchingRef.current = true;
      if (mountedRef.current) setIsLoading(true);

      try {
        const response = await listApiKeys({
          workspaceId: workspaceUuid,
          status: 'active',
          page,
          limit: API_KEYS_PAGE_SIZE,
        });
        if (!mountedRef.current || epoch !== epochRef.current) return;

        totalRef.current = response.pagination.total;
        const nextCount = (replace ? 0 : loadedCountRef.current) + response.items.length;
        loadedCountRef.current = nextCount;
        nextPageRef.current = page + 1;

        const more = nextCount < totalRef.current;
        hasMoreRef.current = more;
        setHasMore(more);
        setError(null);
        setItems((prev) => (replace ? response.items : [...prev, ...response.items]));
      } catch (fetchError: unknown) {
        if (!mountedRef.current || epoch !== epochRef.current) return;
        logger.error('Failed to load API keys', fetchError);
        setError(fetchError);
      } finally {
        // Only the current epoch's fetch owns the loading/fetching flags.
        if (epoch === epochRef.current) {
          if (mountedRef.current) setIsLoading(false);
          isFetchingRef.current = false;
        }
      }
    },
    [workspaceUuid]
  );

  const loadMore = useCallback(() => {
    if (isFetchingRef.current || !hasMoreRef.current) return;
    void fetchPage(nextPageRef.current, false);
  }, [fetchPage]);

  const refresh = useCallback(() => {
    nextPageRef.current = 1;
    loadedCountRef.current = 0;
    void fetchPage(1, true);
  }, [fetchPage]);

  useEffect(() => {
    epochRef.current += 1;
    setItems([]);
    setError(null);
    setHasMore(false);
    hasMoreRef.current = false;
    nextPageRef.current = 1;
    loadedCountRef.current = 0;
    totalRef.current = 0;
    isFetchingRef.current = false;

    if (!workspaceUuid) return;
    void fetchPage(1, true);
  }, [workspaceUuid, fetchPage]);

  return { items, isLoading, error, hasMore, loadMore, refresh };
};

export default useApiKeys;
